import type {
  InspectionReport,
  InspectionReportStatus,
  ReportEventType,
  ReportReadiness,
  ReportNumberPresentation,
  ReportSettings,
  ProcedureRequirementState,
} from '@/services/inspection-report.service';

/**
 * Pure presentation helpers for the Inspection Report UI. These mirror the
 * backend's own state/permission rules for UX gating only — the backend guards
 * remain the source of truth (a hidden action still 403s if forced).
 */

export const REPORT_STATUS_LABELS: Record<InspectionReportStatus, string> = {
  DRAFT: 'Draft',
  GENERATED: 'Generated',
  UNDER_REVIEW: 'Under Review',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
};

export const REPORT_STATUS_BADGE: Record<InspectionReportStatus, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  GENERATED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  UNDER_REVIEW: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  APPROVED: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

export const REPORT_EVENT_LABELS: Record<ReportEventType, string> = {
  DRAFT_CREATED: 'Draft created',
  GENERATED: 'Generated',
  SUBMITTED_FOR_REVIEW: 'Submitted for review',
  REJECTED: 'Rejected',
  REOPENED: 'Reopened',
  APPROVED: 'Approved',
  PUBLISHED: 'Published to customer',
  UNPUBLISHED: 'Unpublished',
  CUSTOMER_DOWNLOADED: 'Customer downloaded',
  DOWNLOADED: 'Downloaded',
};

const EDITABLE_STATUSES: InspectionReportStatus[] = ['DRAFT', 'GENERATED', 'REJECTED'];

export const REPORT_PERMISSIONS = {
  generate: 'inspection:report:generate',
  download: 'inspection:report:download',
  submit: 'inspection:report:submit',
  approve: 'inspection:report:approve',
  reject: 'inspection:report:reject',
  reopen: 'inspection:report:reopen',
  publish: 'inspection:report:publish',
  unpublish: 'inspection:report:unpublish',
} as const;

/** Metadata/photo editing is allowed only in editable states and only for
 * authors (report:submit — excludes OPS, per the frozen matrix). */
export function canEditReport(
  report: Pick<InspectionReport, 'status'>,
  has: (p: string) => boolean,
): boolean {
  return EDITABLE_STATUSES.includes(report.status) && has(REPORT_PERMISSIONS.submit);
}

export type ReportActionKey =
  'generate' | 'submit' | 'approve' | 'reject' | 'reopen' | 'publish' | 'unpublish';

/** Which workflow actions to show for the current state + permissions. Backend
 * state machine + guards enforce these too — this only drives visibility. */
export function availableReportActions(
  report: Pick<InspectionReport, 'status' | 'publishedToCustomer'>,
  has: (p: string) => boolean,
  readiness: ReportReadiness | undefined,
): ReportActionKey[] {
  const s = report.status;
  const actions: ReportActionKey[] = [];

  if (
    (s === 'DRAFT' || s === 'GENERATED' || s === 'REJECTED') &&
    has(REPORT_PERMISSIONS.generate) &&
    (readiness?.canGenerate ?? false)
  ) {
    actions.push('generate');
  }
  if (s === 'GENERATED' && has(REPORT_PERMISSIONS.submit)) actions.push('submit');
  if (s === 'UNDER_REVIEW' && has(REPORT_PERMISSIONS.approve)) actions.push('approve');
  if (s === 'UNDER_REVIEW' && has(REPORT_PERMISSIONS.reject)) actions.push('reject');
  if ((s === 'APPROVED' || s === 'REJECTED') && has(REPORT_PERMISSIONS.reopen)) {
    actions.push('reopen');
  }
  if (s === 'APPROVED' && !report.publishedToCustomer && has(REPORT_PERMISSIONS.publish)) {
    actions.push('publish');
  }
  if (report.publishedToCustomer && has(REPORT_PERMISSIONS.unpublish)) actions.push('unpublish');

  return actions;
}

/**
 * THE single wording for the procedure requirement. The report readiness card
 * and the Procedure Execution panel both read from here, so the two cannot
 * describe the same situation in different words.
 *
 * The STATE itself is no longer derived on the client. Phase 5E made the server
 * authoritative (`readiness.procedureState`), because only the server can see
 * the FROZEN snapshot policy that decides it — a client guessing from a boolean
 * could not tell "no procedure exists" from "optional procedure ignored".
 */
export type { ProcedureRequirementState };

export const PROCEDURE_REQUIREMENT_COPY: Record<
  ProcedureRequirementState,
  { tone: 'warning' | 'error' | 'success'; label: string; help: string }
> = {
  // Rule 1 — the model has no procedure. Nothing to do, nothing blocked.
  NOT_REQUIRED: {
    tone: 'success',
    label: 'No procedure required for this model',
    help: '',
  },
  // Rule 2 — a procedure exists but was published as optional.
  OPTIONAL: {
    tone: 'success',
    label: 'Procedure is optional',
    help: 'This procedure may be completed for the record, but it does not block the report.',
  },
  // Rule 3, unmet.
  MANDATORY_INCOMPLETE: {
    tone: 'error',
    label: 'Complete mandatory procedure before generating report',
    help: 'Finish the procedure in the Procedure Execution panel and mark it complete. Every mandatory STEP must be actioned before the procedure can be completed.',
  },
  // Rule 3, met.
  MANDATORY_COMPLETE: {
    tone: 'success',
    label: 'Mandatory procedure completed',
    help: '',
  },
};

/** Shown under the requirement whenever it is unmet. */
export const PROCEDURE_VS_INTAKE_HELP =
  'Procedure steps are not the same as the inward condition checklist (physical damage, display condition, missing parts).';

export interface ReadinessItem {
  key: keyof Omit<ReportReadiness, 'canGenerate'>;
  label: string;
  met: boolean;
}

export function readinessItems(r: ReportReadiness): ReadinessItem[] {
  return [
    { key: 'customerPresent', label: 'Customer selected', met: r.customerPresent },
    { key: 'equipmentPresent', label: 'Equipment selected', met: r.equipmentPresent },
    { key: 'complaintPresent', label: 'Complaint entered', met: r.complaintPresent },
    {
      key: 'hasCompletedChecklist',
      // The label is a fallback only: the banner replaces it with the
      // state-specific wording from PROCEDURE_REQUIREMENT_COPY.
      label: 'Procedure requirement',
      met: r.hasCompletedChecklist,
    },
    { key: 'hasFinding', label: 'At least one finding entered', met: r.hasFinding },
    { key: 'hasPhoto', label: 'At least one inspection photo uploaded', met: r.hasPhoto },
  ];
}

// ── Report-number live preview (mirrors the backend formatReportNumber) ───────

function separatorChar(sep: ReportSettings['reportNumberSeparator']): string {
  switch (sep) {
    case 'SLASH':
      return '/';
    case 'HYPHEN':
      return '-';
    case 'UNDERSCORE':
      return '_';
    case 'NONE':
      return '';
  }
}

function yearToken(fmt: ReportSettings['reportNumberYearFormat'], year: number): string {
  switch (fmt) {
    case 'NONE':
      return '';
    case 'YY':
      return String(year).slice(-2);
    case 'YYYY':
      return String(year);
  }
}

/** Stands in for the sequence digits in a format pattern. Display only. */
const SEQUENCE_PLACEHOLDER = '#';

/**
 * The report-number FORMAT the tenant has configured, with the sequence shown as
 * a fixed-width placeholder — e.g. prefix, year and six sequence digits.
 *
 * Every part is derived from the tenant's own settings; nothing here is
 * hardcoded. It deliberately shows the SHAPE rather than a predicted number,
 * because the next sequence lives in the allocator's counter and no read API
 * exposes it — so any specific number shown for a not-yet-generated report would
 * be a guess.
 */
export function reportNumberFormatPattern(settings: ReportNumberPresentation): string {
  const sep = separatorChar(settings.reportNumberSeparator);
  const yr = yearToken(settings.reportNumberYearFormat, new Date().getUTCFullYear());
  const seq = SEQUENCE_PLACEHOLDER.repeat(Math.max(1, settings.reportNumberSequenceLength));
  return [settings.reportNumberPrefix.trim(), yr, seq].filter((s) => s.length > 0).join(sep);
}
